import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, KeyRound, Eye, EyeOff } from 'lucide-react'
import { authApi } from '../../lib/api/auth'
import './auth.css'

export default function SetPassword() {
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const token = params.get('token') || ''
  const email = params.get('email') || ''

  const [password, setPassword]           = useState('')
  const [confirm, setConfirm]             = useState('')
  const [showPassword, setShowPassword]   = useState(false)
  const [showConfirm, setShowConfirm]     = useState(false)
  const [loading, setLoading]             = useState(false)
  const [error, setError]                 = useState('')
  const [done, setDone]                   = useState(false)

  // Guard: invalid link
  if (!token || !email) {
    return (
      <div className="forgot-page">
        <div className="forgot-wrapper">
          <div className="forgot-card forgot-success">
            <h2 style={{ color: 'var(--danger)' }}>Invalid Link</h2>
            <p>This account setup link is invalid or has already been used.</p>
            <Link to="/signin" className="btn btn-primary" style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}>
              Go to Sign In
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setError('')
    setLoading(true)
    try {
      await authApi.setupPassword(token, email, password)
      setDone(true)
      setTimeout(() => navigate('/signin', { replace: true }), 3000)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to set password. The link may have expired.')
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div className="forgot-page">
        <div className="forgot-wrapper">
          <div className="forgot-card forgot-success">
            <div className="forgot-success-icon">
              <CheckCircle size={48} color="#48bb78" />
            </div>
            <h2>You're all set!</h2>
            <p>Your password has been created and your account is now active.</p>
            <p className="forgot-hint">Redirecting you to sign in…</p>
            <Link to="/signin" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
              Sign In Now
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="forgot-page">
      <div className="forgot-wrapper">
        <div className="forgot-card">

          <div className="auth-header" style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ background: 'var(--primary-navy)', borderRadius: '50%', padding: 16 }}>
                <KeyRound size={32} color="white" />
              </div>
            </div>
            <h2>Set your password</h2>
            <p>Welcome to Interview Dress Rehearsal Platform! Create a password to activate your interviewer account.</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
              Setting up account for <strong>{email}</strong>
            </p>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div className="password-input">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  className="form-input"
                  placeholder="Repeat your password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary auth-submit" disabled={loading}>
              {loading ? 'Activating account…' : 'Set Password & Sign In'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-muted)' }}>
            Already have an account?{' '}
            <Link to="/signin" style={{ color: 'var(--primary-navy)', fontWeight: 500 }}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
